import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, RiskBadge, StageBadge, StatusBadge, EmptyState } from "@/components/common/Bits";
import { exportCSV, fmtDate } from "@/lib/constants";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Search, Plus, Download, LayoutGrid, Rows3, Database, FlaskConical } from "lucide-react";
import { toast } from "sonner";

const ALL = "__all__";

export default function Registry() {
  const { can } = useAuth();
  const navigate = useNavigate();
  const [rows, setRows] = useState(null);
  const [catalogs, setCatalogs] = useState(null);
  const [view, setView] = useState("table");
  const [q, setQ] = useState("");
  const [filters, setFilters] = useState({ department: ALL, stage: ALL, risk: ALL, provider: ALL, data_classification: ALL });
  const [sort, setSort] = useState("-updated_at");

  const load = async () => {
    const params = { sort };
    if (q) params.q = q;
    Object.entries(filters).forEach(([k, v]) => { if (v !== ALL) params[k] = v; });
    const { data } = await api.get("/initiatives", { params });
    setRows(data);
  };

  useEffect(() => { api.get("/catalogs").then(({ data }) => setCatalogs(data)); }, []);
  useEffect(() => {
    const t = setTimeout(load, q ? 300 : 0);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q, filters, sort]);

  const filterDefs = useMemo(() => catalogs ? [
    { key: "department", label: "Departamento", options: catalogs.departments },
    { key: "stage", label: "Etapa", options: catalogs.stages },
    { key: "risk", label: "Riesgo", options: catalogs.risk_levels },
    { key: "provider", label: "Proveedor", options: catalogs.providers },
    { key: "data_classification", label: "Clasificación", options: catalogs.data_classifications },
  ] : [], [catalogs]);

  const doExport = () => {
    exportCSV(rows.map((r) => ({
      codigo: r.code, nombre: r.name, departamento: r.department, responsable: r.business_owner,
      proveedor: r.provider, modelo: r.model, etapa: r.stage, riesgo_inherente: r.inherent_risk,
      riesgo_residual: r.residual_risk, comite: r.committee_status, clasificacion: r.data_classification,
      valor_esperado: r.expected_value, proxima_revision: r.next_review_date,
    })), "ai_registry_ucchristus.csv");
    toast.success("Registro exportado a CSV");
  };

  return (
    <div>
      <PageHeader title="AI Registry" subtitle="Catálogo institucional de iniciativas, herramientas, modelos y agentes de IA." testId="registry-header">
        <Button variant="outline" onClick={doExport} disabled={!rows?.length} data-testid="export-registry-btn" className="border-border">
          <Download className="h-4 w-4 mr-2" /> Exportar
        </Button>
        {can.edit && (
          <Button onClick={() => navigate("/registro/nueva")} className="bg-primary hover:bg-primary/90 text-primary-foreground" data-testid="new-initiative-btn">
            <Plus className="h-4 w-4 mr-2" /> Nueva iniciativa
          </Button>
        )}
      </PageHeader>

      <div className="flex flex-col gap-3 mb-6">
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative flex-1 min-w-56">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar por iniciativa, responsable, departamento, proveedor o modelo…" className="pl-9 bg-card" data-testid="registry-search" />
          </div>
          <Select value={sort} onValueChange={setSort}>
            <SelectTrigger className="w-44 bg-card" data-testid="registry-sort"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="-updated_at">Más recientes</SelectItem>
              <SelectItem value="name">Nombre A–Z</SelectItem>
              <SelectItem value="-expected_value_clp">Mayor valor</SelectItem>
              <SelectItem value="department">Departamento</SelectItem>
            </SelectContent>
          </Select>
          <ToggleGroup type="single" value={view} onValueChange={(v) => v && setView(v)} className="border border-border rounded-lg bg-card">
            <ToggleGroupItem value="table" aria-label="Tabla" data-testid="view-table-btn"><Rows3 className="h-4 w-4" /></ToggleGroupItem>
            <ToggleGroupItem value="cards" aria-label="Tarjetas" data-testid="view-cards-btn"><LayoutGrid className="h-4 w-4" /></ToggleGroupItem>
          </ToggleGroup>
        </div>
        <div className="flex flex-wrap gap-2">
          {filterDefs.map((f) => (
            <Select key={f.key} value={filters[f.key]} onValueChange={(v) => setFilters((p) => ({ ...p, [f.key]: v }))}>
              <SelectTrigger className="w-auto min-w-36 h-8 text-xs bg-card" data-testid={`filter-${f.key}`}>
                <SelectValue placeholder={f.label} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL}>{f.label}: todos</SelectItem>
                {f.options.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
              </SelectContent>
            </Select>
          ))}
        </div>
      </div>

      {!rows ? (
        <div className="space-y-2">{[...Array(6)].map((_, i) => <Skeleton key={i} className="h-14 rounded-lg" />)}</div>
      ) : rows.length === 0 ? (
        <EmptyState icon={Database} title="Sin resultados" subtitle="Ajuste los filtros o registre una nueva iniciativa." />
      ) : view === "table" ? (
        <div className="bg-card rounded-xl border border-border shadow-card overflow-hidden">
          <div className="overflow-x-auto">
            <Table data-testid="registry-table">
              <TableHeader>
                <TableRow className="hover:bg-transparent">
                  {["Iniciativa", "Departamento", "Proveedor / Modelo", "Etapa", "Riesgo residual", "Comité", "Próxima revisión"].map((h) => (
                    <TableHead key={h} className="text-xs uppercase tracking-wide text-muted-foreground whitespace-nowrap">{h}</TableHead>
                  ))}
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((r) => (
                  <TableRow key={r.id} className="cursor-pointer" onClick={() => navigate(`/iniciativas/${r.id}`)} data-testid={`registry-row-${r.id}`}>
                    <TableCell className="min-w-64">
                      <p className="font-medium text-sm">{r.name}</p>
                      <p className="text-xs text-muted-foreground">{r.code} · {r.business_owner}{r.from_shadow_ai && " · origen Shadow AI"}</p>
                    </TableCell>
                    <TableCell className="text-sm whitespace-nowrap">{r.department}</TableCell>
                    <TableCell className="text-sm whitespace-nowrap">
                      <p>{r.provider}</p>
                      <p className="text-xs text-muted-foreground">{r.model} · v{r.model_version}</p>
                    </TableCell>
                    <TableCell><StageBadge stage={r.stage} /></TableCell>
                    <TableCell><RiskBadge level={r.residual_risk} /></TableCell>
                    <TableCell><StatusBadge status={r.committee_status} /></TableCell>
                    <TableCell className="text-xs text-muted-foreground whitespace-nowrap">{fmtDate(r.next_review_date)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="registry-cards">
          {rows.map((r) => (
            <Card key={r.id} className="shadow-card border-border hover:shadow-float transition-shadow flex flex-col">
              <CardContent className="p-5 flex flex-col flex-1">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <p className="text-xs text-muted-foreground">{r.code} · {r.asset_type}</p>
                    <p className="font-display font-semibold mt-0.5 leading-snug">{r.name}</p>
                  </div>
                  <FlaskConical className="h-4 w-4 text-muted-foreground shrink-0 mt-1" />
                </div>
                <p className="text-xs text-muted-foreground mt-2 line-clamp-2 leading-relaxed">{r.description}</p>
                <div className="flex flex-wrap gap-1.5 mt-4">
                  <StageBadge stage={r.stage} />
                  <RiskBadge level={r.residual_risk} />
                </div>
                <div className="mt-4 pt-4 border-t border-border flex items-center justify-between text-xs text-muted-foreground">
                  <span className="truncate">{r.department} · {r.business_owner}</span>
                </div>
                <Button asChild variant="outline" size="sm" className="mt-auto w-full mt-4 border-border">
                  <Link to={`/iniciativas/${r.id}`}>Abrir ficha</Link>
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
      {rows && <p className="text-xs text-muted-foreground mt-4">{rows.length} iniciativa(s) en el portafolio</p>}
    </div>
  );
}
